import { useEffect, useRef } from 'react';
import { observable } from '@legendapp/state';
type ScrollDirection = 'up' | 'down' | '';
export type UseScrollDirectionOptions = {
  threshold?: number;
  minScrollY?: number;
  anchorIgnoreTimeout?: number;
  resizeIgnoreTimeout?: number;
};
export function useScrollDirection$({
  threshold = 10,
  minScrollY = 0,
  anchorIgnoreTimeout = 500,
  resizeIgnoreTimeout = 500
}: UseScrollDirectionOptions = {}) {
  const scrollDirectionRef = useRef(observable<ScrollDirection>(''));
  const skipScroll = useRef(false);
  useEffect(() => {
    let anchorTimeout: ReturnType<typeof setTimeout> | null = null;
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    const scrollDirection$ = scrollDirectionRef.current;
    let lastScrollY = 0;
    const ticking = {
      current: false
    };
    const updateScrollDirection = () => {
      const scrollY = window.pageYOffset;
      const overMinScrollY = scrollY > minScrollY;
      const overThreshold = Math.abs(scrollY - lastScrollY) >= threshold;
      if (!skipScroll.current && overMinScrollY && overThreshold) {
        const direction: ScrollDirection = scrollY > lastScrollY ? 'down' : 'up';
        lastScrollY = Math.max(0, scrollY);
        scrollDirection$.set(direction);
      }
      ticking.current = false;
    };
    const handleScroll = () => {
      if (ticking.current) {
        return;
      }
      requestAnimationFrame(() => updateScrollDirection());
      ticking.current = true;
    };
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      skipScroll.current = true;
      resizeTimeout = setTimeout(() => {
        skipScroll.current = false;
      }, resizeIgnoreTimeout);
    };
    const handleAnchorClick = (e: MouseEvent) => {
      if (anchorTimeout) {
        clearTimeout(anchorTimeout);
      }
      const target = e.target as HTMLElement;
      if (!target) {
        return;
      }
      const link = target.closest('a');
      if (link && link.getAttribute('href')?.startsWith('#')) {
        skipScroll.current = true;
        anchorTimeout = setTimeout(() => {
          skipScroll.current = false;
        }, anchorIgnoreTimeout);
      }
    };
    const controller = new AbortController();
    window.addEventListener('scroll', handleScroll, {
      passive: true,
      signal: controller.signal
    });
    window.addEventListener('resize', handleResize, {
      passive: true,
      signal: controller.signal
    });
    window.addEventListener('click', handleAnchorClick, {
      passive: true,
      signal: controller.signal
    });
    return () => {
      controller.abort();
      scrollDirection$.set('');
      for (const timeout of [resizeTimeout, anchorTimeout]) {
        if (timeout) {
          clearTimeout(timeout);
        }
      }
    };
  }, [anchorIgnoreTimeout, minScrollY, resizeIgnoreTimeout, threshold]);
  return /* eslint-disable-line react-hooks/refs */scrollDirectionRef.current;
}